import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/shared/ui/button';
import { ArrowLeft, Plus, UserPlus, Menu, X, Loader2, LayoutDashboard, ChevronRight } from 'lucide-react';
import { ServiceLine } from '@/shared/types/serviceLineTypes';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from '@/config/routeConfig';
import { useUser } from '@/contexts/UserContext';
import ContainerLayout from '@/shared/components/ContainerLayout';
import CreateServiceLineDialog from '@/domains/service-lines/dialogs/CreateServiceLineDialog';
import AddCustomerDialog from '@/domains/customers/dialogs/AddCustomerDialog';
import { usePermissions } from '@/shared/hooks/use-permissions';
import { Feature, Permission } from '@/shared/types/permissionsTypes';
import { DashboardTab, refetchDashboardData } from '@/domains/accounts/components/tabs/DashboardTab';
import { SubscriptionsTab } from '@/domains/accounts/components/tabs/SubscriptionsTab';
import { TerminalsTab } from '@/domains/accounts/components/tabs/TerminalsTab';
import { L2VPNCircuitsTab } from '@/domains/accounts/components/tabs/L2VPNCircuitsTab';
import { RouterConfigsTab } from '@/domains/accounts/components/tabs/RouterConfigsTab';
import { CustomersTab, refetchCustomerData } from '@/domains/accounts/components/tabs/CustomersTab';
import { AuditLogsTab } from '@/domains/accounts/components/tabs/AuditLogsTab';
import { SettingsTab } from '@/domains/accounts/components/tabs/SettingsTab';
import { useOrganizationOverview, useGlobalOrganizationOverview } from '@/domains/accounts/services/account-overview';
import { InvoicesTab } from '@/domains/accounts/components/tabs/InvoicesTab';
import { GlobalDashboardDialog } from '@/domains/global-dashboard/dialogs/GlobalDashboardDialog';
import { showError } from '@/shared/utils/toast-utils';
import { OrganizationOverviewData } from '@/shared/types/overviewTypes';
import { Customer } from '@/shared/types/customerTypes';
import { AccountDetailsTab, getTabFromSlug, getVisibleAccountDetailsTabs, isValidAccountDetailsTabSlug } from '@/shared/config/account-details-tabs';
import { CustomerDetailsTab } from '@/shared/config/customer-details-tabs';
import { useQueryClient } from '@tanstack/react-query';
import { useRouteNavigation } from '@/shared/hooks/use-route-navigation';
import { getAccountDetailsPath } from '@/shared/utils/account-details-routes';
import { ERROR } from '@/config/toast-message-config';
import { OrgType } from '@/shared/types/userRoleTypes';
import { useUserRoles } from '@/shared/hooks/userRoles';

function getAccountDetailsTabFromParams(tabSlug?: string): AccountDetailsTab {
  return getTabFromSlug(tabSlug);
}

export const AccountDetailsPage = () => {
  const navigate = useNavigate();
  const { organizationId, tabSlug } = useParams<{ organizationId: string; tabSlug?: string }>();
  const { hasPermission, hasGlobalDashboardAccess } = usePermissions();
  const { currentUser, selectedOrganization, setSelectedOrganizationOnly, isInitialized, isLoading } = useUser();
  const { isPlatformUser } = useUserRoles();

  const activeTab = getAccountDetailsTabFromParams(tabSlug);
  // Sidebar state
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const { navigateToCustomerDetails } = useRouteNavigation();

  const { data: organizationOverview, refetch: refetchOverview } = useOrganizationOverview();
  const globalOverview = useGlobalOrganizationOverview();
  const [globalOrganizationsOverview, setGlobalOrganizationsOverview] = useState<OrganizationOverviewData[]>([]);

  // Sync selectedOrganization from URL when deep-linking or switching organizations
  useEffect(() => {
    if (!organizationId || !currentUser) return;

    const organizationFromUser = currentUser.organizations?.find((a) => a.organizationId === organizationId);
    if (organizationFromUser) {
      if (selectedOrganization?.organizationId !== organizationFromUser.organizationId) {
        setSelectedOrganizationOnly(organizationFromUser, null);
      }
      return;
    }

    // Platform admins (and other deep-links) may have the account only in selectedOrganization storage.
    if (selectedOrganization?.organizationId === organizationId) {
      return;
    }
  }, [organizationId, currentUser, selectedOrganization?.organizationId, setSelectedOrganizationOnly]);

  // Quick Actions state
  const [isCreateServiceLineDialogOpen, setIsCreateServiceLineDialogOpen] = useState(false);
  const [isAddCustomerDialogOpen, setIsAddCustomerDialogOpen] = useState(false);
  const [isGlobalDashboardOpen, setIsGlobalDashboardOpen] = useState(false);

  // Query client for refetching data
  const queryClient = useQueryClient();

  // Handle manage service line
  const handleManageServiceLine = useCallback((serviceLine: ServiceLine) => {
    if (!serviceLine.serviceLineNumber) return;
    navigate(`${ROUTES.SERVICE_LINE_DETAILS.replace(':serviceLineNumber', serviceLine.serviceLineNumber)}`, {
      state: {
        serviceLineNumber: serviceLine.serviceLineNumber,
      }
    });
  }, [navigate]);

  const handleGlobalDashboardButtonAction = async () => {
    try {
      const globalOrganizationOverviewResponse = await globalOverview.mutateAsync();
      if (globalOrganizationOverviewResponse?.data?.results?.length > 0) {
        setGlobalOrganizationsOverview(globalOrganizationOverviewResponse?.data?.results);
        setIsGlobalDashboardOpen(true);
      } else {
        setGlobalOrganizationsOverview([]);
        setIsGlobalDashboardOpen(false);
        showError(ERROR.GLOBAL_DASHBOARD.LOAD_ERROR);
      }
    } catch (error) {
      setGlobalOrganizationsOverview([]);
      setIsGlobalDashboardOpen(false);
      showError(ERROR.GLOBAL_DASHBOARD.LOAD_ERROR);
    }
  };

  // Render content based on active tab
  const renderTabContent = () => {
    switch (activeTab) {
      case AccountDetailsTab.DASHBOARD:
        return <DashboardTab
          organization={selectedOrganization}
          organizationOverviewData={organizationOverview}
          onManageServiceLine={handleManageServiceLine}
        />;

      case AccountDetailsTab.SUBSCRIPTIONS:
        return <SubscriptionsTab
          onManageServiceLine={handleManageServiceLine}
        />;

      case AccountDetailsTab.TERMINALS:
        return <TerminalsTab
          organizationOverviewData={organizationOverview}
          onManageServiceLine={handleManageServiceLine}
        />;

      case AccountDetailsTab.L2VPN_CIRCUITS:
        return <L2VPNCircuitsTab />;

      case AccountDetailsTab.ROUTER_CONFIGS:
        return <RouterConfigsTab />;

      case AccountDetailsTab.CUSTOMERS:
        return (
          <CustomersTab
            organization={selectedOrganization}
            currentUser={currentUser}
            onViewCustomer={(customer: Customer) => navigateToCustomerDetails(customer, CustomerDetailsTab.DASHBOARD)}
            onAddCustomer={() => setIsAddCustomerDialogOpen(true)}
          />
        );

      case AccountDetailsTab.AUDIT_LOGS:
        return <AuditLogsTab />;

      case AccountDetailsTab.INVOICES:
        return <InvoicesTab />;

      case AccountDetailsTab.SETTINGS:
        return <SettingsTab
          currentUser={currentUser}
          selectedOrganization={selectedOrganization}
        />;

      default:
        return <DashboardTab
          organization={selectedOrganization}
          organizationOverviewData={organizationOverview}
          onManageServiceLine={handleManageServiceLine}
        />;
    }
  };

  // If tab is not valid, navigate to account details page with default tab
  if (!tabSlug || !isValidAccountDetailsTabSlug(tabSlug)) {
    return <Navigate to={getAccountDetailsPath(organizationId)} replace />;
  }

  if (!isInitialized || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Missing route param — leave account details
  if (!organizationId) {
    return <Navigate to={isPlatformUser ? ROUTES.ACCOUNTS : ROUTES.LOGIN} replace />;
  }

  // Selection still resolving after reload (avoid bouncing through login → Index → home)
  if (!selectedOrganization || selectedOrganization.organizationId !== organizationId) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // If account is found, render the account details page
  return (
    <ContainerLayout shouldShowSidebar={false} shouldAllowRegionChange={false} shouldAllowAccountChange={true}>
      {/* Header Section - Fixed */}
      <div className="glass-panel flex h-16 flex-row items-center shadow-sm">
        {isPlatformUser && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)} // Go back to previous page
            className="hover:bg-transparent"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        )}
        <div className="flex-1 flex justify-end items-center gap-4 pr-4">
          {selectedOrganization && <h2 className="text-2xl font-bold">{selectedOrganization.name}</h2>}
          {hasGlobalDashboardAccess() && activeTab === AccountDetailsTab.DASHBOARD && (
            <Button variant="default" onClick={handleGlobalDashboardButtonAction} className="gap-2 bg-indigo-500 hover:bg-indigo-600">
              <LayoutDashboard className="h-4 w-4" />
              Global Dashboard
              {globalOverview.isPending && <Loader2 className="h-4 w-4 animate-spin ml-2" />}
            </Button>
          )}
        </div>
      </div>
      <div className="flex flex-row flex-1">
        {/* Sidebar - Fixed height, no scroll */}
        <div className={`${isSidebarCollapsed ? 'w-16' : 'w-80'} glass-panel flex-shrink-0 h-full overflow-hidden border-r transition-all duration-500 ease-in-out`}>
          <div className="p-4 space-y-4">
            {/* Toggle Button */}
            <div className="flex justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                className="h-8 w-8 p-0 hover:bg-muted"
              >
                {isSidebarCollapsed ? <Menu className="h-4 w-4" /> : <X className="h-4 w-4" />}
              </Button>
            </div>

            {/* Quick Actions: Only show if not collapsed and role is not read-only*/}
            {!isSidebarCollapsed && (hasPermission(OrgType.TENANT, Feature.SERVICE_LINE, Permission.MANAGE) || hasPermission(OrgType.TENANT, Feature.CUSTOMERS, Permission.MANAGE)) && (
              <div className="space-y-3">
                {!isSidebarCollapsed && (
                  <h3 className="text-sm font-semibold uppercase tracking-wider">Quick Actions</h3>
                )}
                <div className="space-y-2">
                  {hasPermission(OrgType.TENANT, Feature.SERVICE_LINE, Permission.MANAGE) && (
                    <Button
                      variant="outline"
                      size="sm"
                      className={`${isSidebarCollapsed ? 'w-8 h-8 p-0' : 'w-full justify-start gap-2 text-sm'}`}
                      onClick={() => setIsCreateServiceLineDialogOpen(true)}
                      disabled={!selectedOrganization}
                      title={"Create Service Line"}
                    >
                      <Plus className="h-4 w-4" />
                      Create Service Line
                    </Button>
                  )}
                  {hasPermission(OrgType.TENANT, Feature.CUSTOMERS, Permission.MANAGE) && (
                    <Button
                      variant="outline"
                      size="sm"
                      className={`${isSidebarCollapsed ? 'w-8 h-8 p-0' : 'w-full justify-start gap-2 text-sm'}`}
                      onClick={() => setIsAddCustomerDialogOpen(true)}
                      disabled={!selectedOrganization}
                      title={"Add Customer"}
                    >
                      <UserPlus className="h-4 w-4" />
                      {"Add Customer"}
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Navigation Tabs */}
            <div className="space-y-2">
              {!isSidebarCollapsed && (
                <h3 className="text-sm font-semibold uppercase tracking-wider">Navigation</h3>
              )}
              <div className="space-y-2">
                {getVisibleAccountDetailsTabs(isPlatformUser).map(({ tab, icon: TabIcon }) => (
                  <Button
                    key={tab}
                    variant={activeTab === tab ? "default" : "outline"}
                    className={`${isSidebarCollapsed ? 'w-8 h-8 p-0' : 'w-full justify-start gap-2'}`}
                    onClick={() => navigate(getAccountDetailsPath(selectedOrganization.organizationId, tab))}
                    title={isSidebarCollapsed ? tab : undefined}
                  >
                    <TabIcon className="h-4 w-4" />
                    {!isSidebarCollapsed && (
                      <>
                        {tab}
                      </>
                    )}
                    {!isSidebarCollapsed && <ChevronRight className="h-4 w-4 transition-transform ml-auto" />}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area - Scrollable */}
        <div className="flex-1 flex flex-col min-h-0">
          {/* Tab Content - Scrollable */}
          <div className="flex-1 overflow-y-auto p-4">
            {renderTabContent()}
          </div>
        </div>
      </div>

      {/* Quick Actions Dialogs */}
      {selectedOrganization && (
        <>
          <CreateServiceLineDialog
            open={isCreateServiceLineDialogOpen}
            onOpenChange={setIsCreateServiceLineDialogOpen}
            onSuccess={() => {
              refetchOverview();
              refetchDashboardData(queryClient);
              setIsCreateServiceLineDialogOpen(false);
            }}
            organization={selectedOrganization}
          />
          <AddCustomerDialog
            open={isAddCustomerDialogOpen}
            organization={selectedOrganization}
            currentUserId={currentUser?.userId}
            onOpenChange={setIsAddCustomerDialogOpen}
            onSuccess={() => {
              refetchCustomerData(queryClient);
              setIsAddCustomerDialogOpen(false);
            }}
          />
        </>
      )}

      {hasGlobalDashboardAccess() && globalOrganizationsOverview?.length > 0 && (
        <GlobalDashboardDialog
          currentUser={currentUser}
          organizations={globalOrganizationsOverview}
          open={isGlobalDashboardOpen}
          onOpenChange={setIsGlobalDashboardOpen}
        />
      )}
    </ContainerLayout>
  );
};

export default AccountDetailsPage;
